// src/pages/LoginPage.tsx
import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import { Capacitor } from '@capacitor/core'
import { User } from '@supabase/supabase-js'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser, setSession, updateSubscription } = useAuth()
  
  // Get the intended destination from location state
  const from = location.state?.from || '/app/calendar'
  
  // Store the intended destination in localStorage for persistence
  useEffect(() => {
    if (location.state?.from) {
      localStorage.setItem('redirect_after_login', location.state.from)
    }
  }, [location.state])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password
      })
      
      if (error) {
        throw error
      }
      
      if (data?.session && data?.user?.email) {
        // TEMPORARILY COMMENT OUT SUBSCRIPTION CHECK
        /*
        // Check user's subscription and trial status
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('is_subscribed, is_trial, trial_end_date')
          .ilike('email', data.user.email)
          .single()
        
        if (profileError) {
          console.error('Profile check error:', profileError)
          throw new Error('Unable to verify account status')
        }
        
        // Check access status (combining subscription and trial logic)
        const isSubscribed = profile?.is_subscribed || false
        const isOnTrial = profile?.is_trial || false
        const trialEndDate = profile?.trial_end_date ? new Date(profile.trial_end_date) : null
        const now = new Date()
        
        const hasActiveSubscription = isSubscribed
        const hasActiveTrial = isOnTrial && trialEndDate && now <= trialEndDate
        
        if (isOnTrial && trialEndDate && now > trialEndDate) {
          setError('Your free trial has expired. Please upgrade to continue using the app.')
          setLoading(false)
          return
        }
        
        if (!hasActiveSubscription && !hasActiveTrial) {
          setError('No active subscription found. Please subscribe to access the app.')
          setLoading(false)
          return
        }
        */
        
        setSession(data.session)
        setUser(data.user)
        
        // Get the stored destination or default to calendar
        const redirectTo = localStorage.getItem('redirect_after_login') || '/app/calendar'
        
        // Clear the stored destination
        localStorage.removeItem('redirect_after_login')
        
        // Special handling for affiliate portal
        if (redirectTo === '/affiliate-portal') {
          localStorage.setItem('affiliate_redirect_pending', 'true')
        }
        
        navigate(redirectTo)
      }
    } catch (err: any) {
      console.error('❌ Sign in error:', err)
      setError(err.message || 'Failed to sign in')
    } finally {
      setLoading(false)
    }
  }
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }
  
  return (
<div data-page="login" className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
        {/* Atmospheric lighting effects */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-primary-500/15 blur-[120px] -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gray-300/20 blur-[120px] -z-10"></div>
      
      <div className="w-full max-w-md mb-6 relative z-10">
        <Button as={Link} to="/" variant="ghost" className="flex items-center text-gray-700 hover:text-gray-950 transition-colors">
          <ArrowLeft size={18} className="mr-1" />
          Back to Home
        </Button>
      </div>
      
      <Card className="w-full max-w-md glass-card shadow-lg z-10">
        <CardHeader>
          <CardTitle className="text-center text-2xl font-display font-bold">
            Welcome <span className="gradient-text">Back</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
              {error.includes('trial has expired') && (
                <div className="mt-3 space-y-2">
                  <Button 
                    as={Link} 
                    to="/pricing"
                    className="w-full bg-primary-700 hover:bg-primary-800 text-white font-medium !rounded-lg"
                  >
                    Upgrade Now
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={toggleShowPassword}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-primary-700 hover:text-primary-600 transition-colors hover:underline">
                  Forgot password?
                </Link>
              </div>
              
              <Button
                type="submit"
                fullWidth
                className={`py-3 text-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 !rounded-full ${
                  loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-black hover:bg-gray-900 text-white'
                }`}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Login'}
              </Button>
            </div>
          </form>
          
          <div className="text-center text-sm text-gray-700 mt-4">
            Don't have an account?{' '}
            <Link to="/trial" className="text-primary-700 hover:text-primary-600 transition-colors hover:underline">
              Start Free Trial
            </Link>
            {' or '}
            <Link to="/pricing" className="text-primary-700 hover:text-primary-600 transition-colors hover:underline">
              View Pricing
            </Link>
          </div>
          
          {/* Debug mode button for mobile */}
          {Capacitor.isNativePlatform() && (
            <div className="mt-8 pt-4 border-t border-gray-200">
              <Button
                type="button"
                onClick={() => {
                  // Create a properly typed debug user with required fields
                  const debugUser = { 
                    id: 'debug-user', 
                    email: 'debug@example.com',
                    app_metadata: {},
                    user_metadata: {},
                    aud: 'authenticated',
                    created_at: new Date().toISOString(),
                    role: '',
                    updated_at: new Date().toISOString()
                  } as User;
                  
                  // Update auth context
                  setUser(debugUser);
                  updateSubscription(true);
                  
                  // Store in localStorage to persist across refreshes
                  localStorage.setItem('debug_mode', 'true');
                  localStorage.setItem('debug_user', JSON.stringify(debugUser));
                  
                  // Redirect to app
                  navigate('/app/calendar');
                }}
                fullWidth
                className="py-5 bg-yellow-500 hover:bg-yellow-600 text-white font-bold text-xl !rounded-xl"
              >
                DEBUG MODE (MOBILE TESTING ONLY)
              </Button>
              <p className="text-xs text-center mt-2 text-gray-500">
                This bypasses authentication for mobile testing.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default LoginPage